import {Close, Delete, Person, Warning} from "@mui/icons-material";
import {Alert, Avatar, Card, CardContent, Chip, Snackbar, TextField} from "@mui/material";
import Box from '@mui/material/Box';
import IconButton from "@mui/material/IconButton";
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import * as React from 'react';
import {useContext, useState} from "react";
import {CommonContext} from "../../../contexts/CommonContext";
import useAxios from "../../../hooks/useAxios";
import {User} from '../../../Models/User';
import {REMOVE_PARTICIPANT_URL} from "../../../utils/constant";
import {AdminProjectContext} from "../../../contexts/AdminProjectContext";
import styled from "styled-components";
import {ParticipantData} from "../../../interface/Participant.interface";

// Styled Components pour le design moderne du modal
const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 500px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  outline: none;
  animation: slideInUp 0.4s ease-out;

  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translate(-50%, -40%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid rgba(244, 67, 54, 0.1);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const WarningIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(45deg, #f44336, #ff5722);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(244, 67, 54, 0.3);

  .MuiSvgIcon-root {
    color: white;
    font-size: 1.2rem;
  }
`;

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    background: rgba(244, 67, 54, 0.1);
    color: #f44336;
    width: 40px;
    height: 40px;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(244, 67, 54, 0.2);
      transform: scale(1.1);
    }
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const ParticipantCard = styled(Card)`
  background: rgba(244, 67, 54, 0.05) !important;
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 16px !important;
  margin-bottom: 2rem;
  box-shadow: none !important;
`;

const ParticipantCardContent = styled(CardContent)`
  padding: 1.5rem !important;
`;

const ParticipantInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ParticipantAvatar = styled(Avatar)`
  width: 50px !important;
  height: 50px !important;
  background: linear-gradient(45deg, #f44336, #ff5722) !important;
  font-weight: 700 !important;
`;

const ParticipantDetails = styled.div`
  flex: 1;
`;

const ParticipantName = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
`;

const ParticipantChip = styled(Chip)`
  &.MuiChip-root {
    background: rgba(244, 67, 54, 0.1);
    color: #f44336;
    border: 1px solid rgba(244, 67, 54, 0.3);
    font-weight: 600;
  }
`;

const WarningMessage = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const WarningText = styled.p`
  color: #f57c00;
  font-weight: 600;
  margin: 0;
  font-size: 1rem;
`;

const ConfirmationField = styled(TextField)`
  margin-bottom: 2rem;

  & .MuiOutlinedInput-root {
    background: rgba(255, 255, 255, 0.9);
    border-radius: 12px;

    &.Mui-focused {
      background: rgba(255, 255, 255, 1);

      & .MuiOutlinedInput-notchedOutline {
        border-color: #f44336;
        border-width: 2px;
      }
    }

    &.Mui-error {
      & .MuiOutlinedInput-notchedOutline {
        border-color: #f44336;
      }
    }
  }

  & .MuiInputLabel-root {
    color: #f44336;
    font-weight: 600;

    &.Mui-focused {
      color: #f44336;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const DeleteButton = styled(Button)`
  background: linear-gradient(45deg, #f44336, #ff5722) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 8px 25px rgba(244, 67, 54, 0.3) !important;

  &:hover {
    background: linear-gradient(45deg, #d32f2f, #e64a19) !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 12px 35px rgba(244, 67, 54, 0.4) !important;
  }

  &:disabled {
    background: rgba(0, 0, 0, 0.1) !important;
    color: rgba(0, 0, 0, 0.4) !important;
    transform: none !important;
    box-shadow: none !important;
  }
`;

const CancelButton = styled(Button)`
  background: rgba(0, 0, 0, 0.1) !important;
  color: var(--text-primary) !important;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;

  &:hover {
    background: rgba(0, 0, 0, 0.15) !important;
    transform: translateY(-1px) !important;
  }
`;

const StyledAlert = styled(Alert)`
  background: rgba(244, 67, 54, 0.1) !important;
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 12px !important;
  margin-bottom: 1rem;

  & .MuiAlert-message {
    color: #f44336;
    font-weight: 500;
  }
`;

export default function AdminParticipantSettingModal(props: {
    openModal: boolean,
    setOpenModal: Function,
    participant?: ParticipantData
}) {
    const onClose = () => props.setOpenModal(false);

    const {selectedProject, refreshProjects} = useContext(AdminProjectContext);
    const {setNeedToRefreshData} = useContext(CommonContext);
    const {deleteWithAxios} = useAxios();

    const [globalError, setGlobalError] = useState('');
    const [globalInfo, setGlobalInfo] = useState('');
    const [formData, setFormData] = useState<{ confirmation: string }>({confirmation: ''});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClose = (event: React.MouseEvent, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason !== 'backdropClick') {
            onClose();
        }
    };

    const handleRemoveParticipant = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (isSubmitting) return;

        // Validation des données requises
        const projectId = selectedProject?.getProjectId();
        if (!projectId) {
            setGlobalError("Projet non sélectionné");
            return;
        }

        if (!props.participant?.uuid) {
            setGlobalError("Participant invalide");
            return;
        }

        setIsSubmitting(true);
        setGlobalError('');

        try {
            await deleteWithAxios(REMOVE_PARTICIPANT_URL(projectId, props.participant.uuid));
            setGlobalError("");
            setGlobalInfo("Adhérent retiré avec succès");
            setNeedToRefreshData(true);

            // Rafraîchir la liste des projets avec les participants
            await refreshProjects();

            onClose();
        } catch (err: any) {
            console.error("Failed to remove participant:", err);
            setGlobalError(err.message || "Erreur lors de la suppression");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={props.openModal}
            onClose={handleClose}
            aria-labelledby="modal-delete-participant"
            aria-describedby="modal-delete-participant-description"
        >
            <ModalContainer>
                <ModalHeader>
                    <ModalTitle>
                        <WarningIcon>
                            <Warning/>
                        </WarningIcon>
                        Supprimer un adhérent
                    </ModalTitle>
                    <CloseButton onClick={onClose}>
                        <Close/>
                    </CloseButton>
                </ModalHeader>

                <ModalBody>
                    {globalError !== '' && (
                        <StyledAlert severity="error">{globalError}</StyledAlert>
                    )}

                    <Snackbar
                        open={globalInfo !== ''}
                        autoHideDuration={6000}
                        onClose={() => setGlobalInfo('')}
                        message={globalInfo}
                    />

                    <ParticipantCard>
                        <ParticipantCardContent>
                            <ParticipantInfo>
                                <ParticipantAvatar>
                                    {props.participant?.firstname?.charAt(0)}
                                    {props.participant?.lastname?.charAt(0)}
                                </ParticipantAvatar>
                                <ParticipantDetails>
                                    <ParticipantName>
                                        {props.participant?.firstname} {props.participant?.lastname}
                                    </ParticipantName>
                                    <ParticipantChip
                                        label={`${props.participant?.totalShare || 0} parts`}
                                        icon={<Person/>}
                                        size="small"
                                    />
                                </ParticipantDetails>
                            </ParticipantInfo>
                        </ParticipantCardContent>
                    </ParticipantCard>

                    <WarningMessage>
                        <WarningText>
                            ⚠️ Cette action est irréversible. L'adhérent sera définitivement retiré du projet.
                        </WarningText>
                    </WarningMessage>

                    <ConfirmationField
                        fullWidth
                        required
                        variant="outlined"
                        label="Confirmez la suppression"
                        placeholder="CONFIRMER LA SUPPRESSION"
                        helperText="Tapez 'CONFIRMER LA SUPPRESSION' pour confirmer"
                        value={formData.confirmation}
                        onChange={(e) => setFormData({confirmation: e.target.value.toString()})}
                        error={formData.confirmation !== "" && formData.confirmation !== "CONFIRMER LA SUPPRESSION"}
                    />

                    <ButtonContainer>
                        <CancelButton onClick={onClose} disabled={isSubmitting}>
                            Annuler
                        </CancelButton>
                        <DeleteButton
                            disabled={formData.confirmation !== "CONFIRMER LA SUPPRESSION" || isSubmitting}
                            onClick={handleRemoveParticipant}
                            startIcon={<Delete/>}
                        >
                            {isSubmitting ? 'Suppression...' : "Supprimer l'adhérent"}
                        </DeleteButton>
                    </ButtonContainer>
                </ModalBody>
            </ModalContainer>
        </Modal>
    );
}
